import { Fragment, useEffect, useState } from 'react';
import { supabase } from '../supabase.js';
import { formatDay, orDash, partnerStatus, partnerStatuses, setPartner, webLink } from './adminData.js';
import EmailDetail from './EmailDetail.jsx';
import { EmailLink, PhoneLink } from './ProviderProspects.jsx';
import { fetchMailbox, formatWhen } from './staffAdmin.js';

const blankForm = {
  status: 'accepted', referral_email: '', contact_name: '', contact_role: '', contact_email: '', contact_phone: '', requirements: '', note: '',
  reply_email_id: '', replied_on: '',
};
const fields = [
  ['referral_email', 'Send referrals to', 'email', 'referrals@provider.org.uk'],
  ['contact_name', 'Contact name', 'text', 'Jo Smith'],
  ['contact_role', 'Their role', 'text', 'Office administrator'],
  ['contact_email', 'Contact email', 'email', 'jo@provider.org.uk'],
  ['contact_phone', 'Contact phone', 'tel', '0113 496 0000'],
];

const asList = (value) => (Array.isArray(value) ? value : []);

/**
 * What the partner's own website and documents say, in sections of plain points, with the pages it came from and the day it was
 * checked. Nothing when there is no profile yet.
 */
function PartnerProfile({ profile, organisation }) {
  if (!profile || typeof profile !== 'object') return null;
  const sections = asList(profile.sections).filter((section) => section?.title && asList(section.items).length > 0);
  const sources = asList(profile.sources).map((source) => ({ ...source, link: webLink(source?.url) })).filter((source) => source.link);
  return (
    <section className="admin-partner-profile" aria-label={`${organisation} from their website`}>
      <h3 className="admin-subsection">From their website</h3>
      {profile.summary && <p className="admin-partner-summary">{profile.summary}</p>}
      <div className="admin-partner-sections">
        {sections.map((section) => (
          <div key={section.title}>
            <h4>{section.title}</h4>
            <ul>{asList(section.items).map((item) => <li key={item}>{item}</li>)}</ul>
          </div>
        ))}
      </div>
      {(sources.length > 0 || profile.checked_on) && (
        <p className="admin-sub">
          {profile.checked_on ? `Checked ${formatDay(profile.checked_on)}` : 'Sources'}
          {sources.map((source) => (
            <Fragment key={source.link.href}>
              {' · '}
              <a className="admin-ext" href={source.link.href} target="_blank" rel="noreferrer">{source.title || source.link.domain}</a>
            </Fragment>
          ))}
        </p>
      )}
    </section>
  );
}

/** Recording or changing a reply: what they said, where referrals go, who to talk to, what a referral needs, and the reply it came from. */
function PartnerForm({ provider, partner, replies, busy, error, onSave, onCancel }) {
  const [form, setForm] = useState(() => (partner
    ? Object.fromEntries(Object.keys(blankForm).map((key) => [key, partner[key] ?? (key === 'status' ? 'accepted' : '')]))
    : blankForm));
  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const pickReply = (event) => {
    const reply = replies.find((email) => email.id === event.target.value);
    setForm((current) => ({ ...current, reply_email_id: event.target.value, replied_on: reply ? String(reply.at).slice(0, 10) : current.replied_on }));
  };
  const save = (event) => {
    event.preventDefault();
    onSave(form);
  };
  const title = partner ? `Change ${provider.organisation}` : `Record ${provider.organisation}'s reply`;
  return (
    <form className="admin-partner-form" onSubmit={save} aria-label={title}>
      <h3 className="admin-subsection">{title}</h3>
      <div className="admin-partner-grid">
        <label>What they said
          <select value={form.status} onChange={set('status')}>
            {partnerStatuses.map(([value, words]) => <option key={value} value={value}>{words}</option>)}
          </select>
        </label>
        {fields.map(([key, label, type, placeholder]) => (
          <label key={key}>{label}
            <input type={type} value={form[key]} onChange={set(key)} placeholder={placeholder} maxLength={200}/>
          </label>
        ))}
        <label>Their reply
          <select value={form.reply_email_id} onChange={pickReply}>
            <option value="">{replies.length > 0 ? 'Choose the email they replied with' : 'No outreach replies found'}</option>
            {replies.map((email) => <option key={email.id} value={email.id}>{`${email.from} · ${email.subject} · ${formatWhen(email.at)}`}</option>)}
          </select>
        </label>
        <label>Replied on
          <input type="date" value={form.replied_on} onChange={set('replied_on')}/>
        </label>
      </div>
      <label className="admin-partner-wide">What a referral must include
        <textarea rows={2} value={form.requirements} onChange={set('requirements')} maxLength={1000} placeholder="Contact number, full name, reason for homelessness"/>
      </label>
      <label className="admin-partner-wide">Note
        <textarea rows={2} value={form.note} onChange={set('note')} maxLength={1000} placeholder="Anything else from their reply"/>
      </label>
      {error && <p className="mod-error" role="alert">{error}</p>}
      <div className="admin-head-actions">
        <button type="submit" className="button button-dark" disabled={busy}>{partner ? 'Save changes' : 'Save'}</button>
        <button type="button" className="button button-secondary" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

/**
 * The partner part of a housing provider's details: what they said to Naima, where referrals go, who to talk to, what a referral
 * must include, their reply and what their website says, with Change and Take off the list. A provider that hasn't answered yet
 * gets "Record their reply". `onChanged` runs after a save or a removal, so the list can be read again.
 */
export function PartnerSection({ provider, partner, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [replies, setReplies] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Naima's replies, for choosing the one a partner came from. Only read when the form is opened.
  useEffect(() => {
    if (!editing) return undefined;
    let live = true;
    fetchMailbox(supabase, 'naima').then(
      (mailbox) => { if (live) setReplies(Array.isArray(mailbox?.received) ? mailbox.received : []); },
      () => { if (live) setReplies([]); },
    );
    return () => { live = false; };
  }, [editing]);

  async function save(form) {
    setBusy(true);
    setError('');
    try {
      const { status, ...details } = form;
      await setPartner(supabase, provider.id, status, details);
      setNotice(`${provider.organisation}: ${partnerStatus(status).label.toLowerCase()}.`);
      setEditing(false);
      onChanged?.();
    } catch (cause) {
      setError(cause.message || 'Could not save that. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError('');
    try {
      await setPartner(supabase, provider.id, null);
      setNotice(`${provider.organisation} is off the partners list.`);
      onChanged?.();
    } catch (cause) {
      setError(cause.message || 'Could not remove that partner. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  const start = () => { setNotice(''); setError(''); setEditing(true); };

  if (editing) {
    return (
      <div className="prov-partner">
        <PartnerForm provider={provider} partner={partner} replies={replies} busy={busy} error={error}
          onSave={(form) => void save(form)} onCancel={() => { setEditing(false); setError(''); }}/>
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="prov-partner">
        {notice && <p className="admin-notice" role="status">{notice}</p>}
        {error && <p className="mod-error" role="alert">{error}</p>}
        <button type="button" className="button button-secondary admin-undo" onClick={start}>Record their reply</button>
      </div>
    );
  }

  const status = partnerStatus(partner.status);
  return (
    <section className="prov-partner" aria-label={`${provider.organisation} as a partner`}>
      <h3 className="admin-subsection">Partner</h3>
      {notice && <p className="admin-notice" role="status">{notice}</p>}
      {error && <p className="mod-error" role="alert">{error}</p>}
      <div className="prov-details">
        <dl>
          <dt>What they said</dt><dd><span className={`admin-status is-${status.tone}`}>{status.label}</span></dd>
          <dt>Replied</dt><dd>{partner.replied_on ? formatDay(partner.replied_on) : '–'}</dd>
        </dl>
        <dl>
          <dt>Send referrals to</dt>
          <dd>
            <EmailLink value={partner.referral_email || partner.email}/>
            {!partner.referral_email && <span className="admin-sub">Their main email</span>}
          </dd>
          <dt>A referral needs</dt><dd>{orDash(partner.requirements)}</dd>
        </dl>
        <dl>
          <dt>Contact</dt>
          <dd>
            {partner.contact_name || '–'}
            {partner.contact_role && <span className="admin-sub">{partner.contact_role}</span>}
          </dd>
          <dt>Contact email</dt><dd><EmailLink value={partner.contact_email}/></dd>
          <dt>Contact phone</dt><dd><PhoneLink value={partner.contact_phone}/></dd>
        </dl>
      </div>
      {partner.note && <p className="admin-partner-note">{partner.note}</p>}
      <PartnerProfile profile={partner.profile} organisation={provider.organisation}/>
      {partner.reply_email_id
        ? (
          <>
            <h3 className="admin-subsection">Their reply</h3>
            <EmailDetail id={partner.reply_email_id} kind="received"/>
          </>
        )
        : <p className="mod-note">No reply email is linked. Change the partner to choose it.</p>}
      <div className="admin-head-actions">
        <button type="button" className="button button-secondary admin-undo" disabled={busy} onClick={start}>Change</button>
        <button type="button" className="button button-secondary admin-undo" disabled={busy} onClick={() => void remove()}>Take off the list</button>
      </div>
    </section>
  );
}
